import { sameOrderComparator } from '@ardoq/common-helpers';
import {
  ColDef,
  ICellRendererParams,
  ITooltipParams,
  NestedFieldPaths,
  SortDirection,
  ValueGetterParams,
} from 'ag-grid-community';
import { CustomCellEditorProps } from 'ag-grid-react';
import {
  AlignedColumn,
  AnyObjectColumn,
  Column,
  GroupingColumn,
  PinnedColumns,
  PrimitiveColumn,
  RegularColumn,
  Row,
} from './types';
import { ColumnHeaderWithIcon } from './ColumnHeaderWithIcon';
import { getValueSetter, isGroupingColumn, isObjectColumn } from './utils';
import { getMenuItems } from './menuItems';

export type PartialInitialColumnState<RowData> = {
  reorderableEnabled: boolean;
  groupedColumns: string[];
  pinnedColumns: PinnedColumns<RowData> | null;
  sortableDescriptor: { colId: string | null; sort: SortDirection } | null;
  enableColumnChooser: boolean;
  suppressAllHeaderMenus: boolean;
  visibleColumnIds: (keyof RowData)[] | null;
};

export type InitialColumnState<RowData> = PartialInitialColumnState<RowData> & {
  groupingEnabled: boolean;
};

type GetColumnDefsOptions<RowData, RowId extends string = string> = {
  columns: Column<RowData, RowId>[];
  columnOrder: (keyof RowData)[] | null;
  initialColumnState: InitialColumnState<RowData>;
  suppressFlexDefault?: boolean;
};

const getHeaderClass = (column: AlignedColumn): string | undefined => {
  if (column.align === 'center') {
    return 'ag-header-cell-align-center';
  }

  if (column.align === 'right') {
    return 'ag-header-cell-align-right';
  }

  return undefined;
};

const getCellClass = (column: AlignedColumn): string | undefined => {
  if (column.align === 'center') {
    return 'ag-cell-align-center';
  }

  if (column.align === 'right') {
    return 'ag-cell-align-right';
  }

  return undefined;
};

const getFlexDef = <RowData, RowId extends string = string>(
  column: Pick<PrimitiveColumn<RowData, keyof RowData, RowId>, 'flex' | 'width'>
): { flex: number } | Record<string, never> => {
  if (column.flex !== undefined) {
    return { flex: column.flex };
  }
  if (!column.width) {
    return { flex: 1 };
  }
  // Don't set flex if only width is provided, it will override the static width
  return {};
};

export const withoutDefaultFlex = (
  { flex: _defaultFlex, ...colDef }: ColDef,
  column: { flex?: number }
): ColDef =>
  column.flex !== undefined ? { ...colDef, flex: column.flex } : colDef;

export const primitiveColumnDef = <
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
>(
  column: PrimitiveColumn<RowData, Key, RowId>
): ColDef<Row<RowData, RowId>> => {
  const columnId = column.id as NestedFieldPaths<Row<RowData, RowId>>;
  const valueGetter = (params: ValueGetterParams<Row<RowData, RowId>>) =>
    params.data ? params.data.data[column.id] : null;
  const headerName = column.title;
  const headerClass = getHeaderClass(column);
  const cellClass = getCellClass(column);

  // In-line editing
  const onCellValueChange = column.onCellValueChange;
  const inLineEditingProps = onCellValueChange
    ? {
        editable: true,
        valueSetter: getValueSetter<RowData, RowId>(onCellValueChange),
      }
    : undefined;

  const headerTooltip = column.tooltip?.headerTooltip;
  const tooltipValueGetter = column.tooltip?.cellTooltip
    ? (params: ITooltipParams<Row<RowData, RowId>>) => {
        const data = params.data?.data;
        if (data) {
          return column.tooltip?.cellTooltip?.(data);
        }
        return null;
      }
    : undefined;

  return {
    field: columnId,
    cellClass,
    headerClass,
    headerName,
    headerTooltip,
    ...(column.headerIcon && {
      headerComponentParams: {
        innerHeaderComponent: ColumnHeaderWithIcon,
        headerIcon: column.headerIcon,
      },
    }),
    // Note: contextMenuItems is intentionally NOT set here.
    // AG Grid caches column-level contextMenuItems and doesn't update them when props change.
    // Instead, we use the grid-level getContextMenuItems callback which reads from columnsRef.current.
    valueGetter,
    tooltipValueGetter,
    suppressHeaderMenuButton: column.suppressHeaderContextMenu,
    suppressHeaderContextMenu: column.suppressHeaderContextMenu,
    resizable: column.resizable,
    ...inLineEditingProps,
    ...(column.width && { width: column.width }),
    ...(column.minWidth && { minWidth: column.minWidth }),
    ...(column.maxWidth && { maxWidth: column.maxWidth }),
    ...getFlexDef(column),
    ...(column.tooltip && {
      tooltipComponent: (params: ITooltipParams<Row<RowData, RowId>>) => {
        if (params.location === 'header') {
          return params.value;
        }

        const rowData = params.data?.data;

        if (rowData && column.tooltip?.cellTooltip) {
          return column.tooltip.cellTooltip(rowData);
        }

        return null;
      },
    }),
    ...(column.comparator && {
      comparator: (_a, _b, a, b) =>
        column.comparator!(
          a.data?.data[column.id] ?? null,
          b.data?.data[column.id] ?? null
        ),
    }),
    ...(column.autoHeight && { autoHeight: true, wrapText: true }),
  };
};

export const sharedColumnDef = <RowData, RowId extends string = string>(
  column: RegularColumn<RowData, RowId> | GroupingColumn<RowData, RowId>,
  index: number,
  {
    reorderableEnabled,
    groupingEnabled,
    groupedColumns,
    pinnedColumns,
    sortableDescriptor,
    enableColumnChooser,
    suppressAllHeaderMenus,
    visibleColumnIds,
  }: InitialColumnState<RowData>
): ColDef<Row<RowData, RowId>> => {
  const isFirstColumn = index === 0;
  const rowDrag = reorderableEnabled && !groupingEnabled && isFirstColumn;
  const enableRowGroup = column.enableRowGroup;
  const columnId = column.id as keyof RowData;
  const columnIdString = String(columnId);
  const rowGroup = groupedColumns.includes(columnIdString);
  const isHidden = visibleColumnIds
    ? !visibleColumnIds.includes(columnId) || rowGroup
    : false;
  const rowGroupIndex = rowGroup
    ? groupedColumns.indexOf(columnIdString)
    : undefined;
  const pinned = pinnedColumns?.[columnId] || undefined;
  const sortable = sortableDescriptor ? !column.suppressSorting : false;
  const sort =
    sortableDescriptor?.colId === columnId
      ? sortableDescriptor.sort
      : undefined;

  return {
    lockPinned: pinnedColumns === null ? true : undefined,
    sortable,
    suppressHeaderMenuButton:
      suppressAllHeaderMenus || column.suppressHeaderContextMenu,
    suppressHeaderContextMenu:
      suppressAllHeaderMenus || column.suppressHeaderContextMenu,
    mainMenuItems: params =>
      getMenuItems(
        params,
        pinnedColumns !== null,
        enableColumnChooser,
        groupedColumns.includes(columnIdString)
      ),
    rowDrag,
    enableRowGroup,
    rowGroup,
    rowGroupIndex,
    hide: isHidden,
    pinned,
    sort,
  };
};

export const objectColumnDef = <
  RowData,
  Key extends keyof RowData,
  RowId extends string = string,
>(
  column: AnyObjectColumn<RowData, Key, RowId>
): ColDef<Row<RowData, RowId>> => {
  const colDef = primitiveColumnDef(column);

  const cellRenderer = 'cellRenderer' in column && column.cellRenderer;
  if (cellRenderer) {
    colDef.cellRenderer = (
      params: ICellRendererParams<Row<RowData, RowId>>
    ) => {
      const data = params.data?.data[column.id];
      if (data) {
        return cellRenderer(data);
      }
      return null;
    };
  }

  const valueGetter = column.valueGetter;
  if (valueGetter) {
    colDef.valueGetter = (params: ValueGetterParams<Row<RowData, RowId>>) => {
      const data = params.data?.data[column.id];
      if (data) {
        return valueGetter(data);
      }
      return null;
    };
  }

  const cellEditor = column.cellEditor;
  if (cellEditor) {
    // When the cell is done editing the value will be emitted via onCellValueChanged.
    colDef.cellEditor = (
      params: CustomCellEditorProps<Row<RowData, RowId>>
    ) => {
      const data = params.data?.data[column.id];
      const { value, onValueChange } = params;
      if (data) {
        return cellEditor({ data, value, onValueChange });
      }
      return null;
    };
  }

  return colDef;
};

export const getColumnDefs = <RowData, RowId extends string = string>({
  columns,
  columnOrder,
  initialColumnState,
  suppressFlexDefault = false,
}: GetColumnDefsOptions<RowData, RowId>): ColDef<Row<RowData, RowId>>[] => {
  // Filter out grouping columns and cast to RegularColumn
  const regularColumns = columns.filter(
    (column): column is RegularColumn<RowData, RowId> =>
      !isGroupingColumn(column)
  );

  // Sort columns according to columnOrder if provided
  const orderedColumns = columnOrder
    ? [...regularColumns].sort(
        sameOrderComparator(
          'id',
          columnOrder.map(id => ({ id }))
        )
      )
    : regularColumns;

  return orderedColumns.map((column, index): ColDef<Row<RowData, RowId>> => {
    const columnDef = isObjectColumn<RowData, RowId>(column)
      ? objectColumnDef(column)
      : primitiveColumnDef(
          column as PrimitiveColumn<RowData, keyof RowData, RowId>
        );

    const sharedColumnDefs = sharedColumnDef(column, index, initialColumnState);
    const colDef = { ...columnDef, ...sharedColumnDefs };
    return suppressFlexDefault ? withoutDefaultFlex(colDef, column) : colDef;
  });
};
